// src/pages/roster/RosterPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  collection, onSnapshot, doc, getDoc, addDoc, updateDoc,
  deleteDoc, serverTimestamp, query, orderBy, where,
} from 'firebase/firestore';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  UserPlusIcon, ArrowUpTrayIcon, MagnifyingGlassIcon,
  PencilSquareIcon, TrashIcon, ChevronLeftIcon,
  ClipboardDocumentListIcon, UserGroupIcon,
  PaperAirplaneIcon, CheckBadgeIcon, ClockIcon,
  CheckCircleIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';

import PlayerModal from './PlayerModal';
import ImportModal from './ImportModal';
import InviteParentForPlayerModal from '@/components/invite/InviteParentForPlayerModal';
import { createInvite, buildInviteUrl, sendInviteDelivery } from '@/services/inviteService';
import { track, ACTIVATION_EVENTS } from '@/lib/analytics';
import { useRole } from '@/context/RoleContext';
import TeamSubNav from '@/components/team/TeamSubNav';
import ChannelBadges from '@/components/roster/ChannelBadges';
import PlayerAvatar from '@/components/roster/PlayerAvatar';
import useParentChannelStatus from '@/hooks/useParentChannelStatus';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/* ---- Parent view ----
 *
 * Parents reach this page via the ViewTeam parent block ("Roster" quick
 * link). They get a stripped-down, read-only view of teammate names —
 * NO other-parent contact info (privacy), NO add/import/edit/delete,
 * NO language toggles, NO bulk actions. Search is restricted to player
 * name only (not parent email).
 */
function ParentRosterView({ team, players, loading, teamId, navigate }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const filtered = players.filter((p) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase().includes(term);
  });

  return (
    <div className="min-h-screen bg-[#f7faf5]">
      {/* Standardized container — same max-w-5xl + padding as ViewTeam,
          TeamSeasonsList, and SeasonSchedule so the four team sub-pages
          feel like one consistent surface when the user tabs between
          them via TeamSubNav. */}
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">

        {/* Team-page sub-navigation. The "Back" button below is
            redundant once the tabs render Overview as a peer, but
            we keep it for now to give parents a clear escape
            hatch back to the team summary. */}
        <TeamSubNav teamId={teamId} />

        {/* Back link */}
        <button
          onClick={() => navigate(`/teams/${teamId}`)}
          className="mb-3 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          {team?.name || t('roster.teamFallback')}
        </button>

        {/* Header */}
        <header className="mb-6">
          <p
            className="text-xs font-semibold uppercase tracking-[0.18em] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('roster.eyebrowTeam')}
          </p>
          <h1
            className="mt-1 text-2xl sm:text-3xl font-bold uppercase tracking-tight text-[#0d1f14]"
            style={{ fontFamily: HEAD_FONT }}
          >
            {t('roster.titleParent')}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            {t('roster.playersOnThisTeam', { count: players.length })}
          </p>
        </header>

        {/* Search by name */}
        {players.length > 3 && (
          <div className="relative mb-4 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="text"
              placeholder={t('roster.searchByName')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-zinc-200 bg-white pl-9 pr-4 py-2 text-sm text-[#0d1f14] placeholder-zinc-400 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
            />
          </div>
        )}

        {/* Body */}
        {loading ? (
          <div className="space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 rounded-2xl bg-zinc-100 animate-pulse" />
            ))}
          </div>
        ) : players.length === 0 ? (
          <div className="rounded-2xl border border-zinc-200 bg-white p-8 sm:p-10 text-center shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40">
              <UserGroupIcon className="h-7 w-7 text-[#5a8a1f]" />
            </div>
            <h2
              className="text-lg font-bold uppercase tracking-wide text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {t('roster.noPlayersYet')}
            </h2>
            <p className="mt-2 text-sm text-zinc-500">
              {t('roster.noPlayersYetParentDesc')}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-sm text-zinc-400">
            {t('roster.noMatches')}
          </div>
        ) : (
          <ul className="space-y-2">
            {filtered.map((player) => (
              <li
                key={player.id}
                className="flex items-center gap-4 rounded-2xl border border-zinc-200 bg-white px-4 sm:px-5 py-3 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]"
              >
                {/* Photo + jersey badge (falls back to jersey circle / initials) */}
                <PlayerAvatar
                  photoURL={player.photoURL}
                  jerseyNumber={player.jerseyNumber}
                  firstName={player.firstName}
                  lastName={player.lastName}
                  alt={t('roster.photoAlt', { name: `${player.firstName || ''} ${player.lastName || ''}`.trim() })}
                />

                {/* Name + position */}
                <div className="min-w-0 flex-1">
                  <p className="text-sm sm:text-base font-semibold text-[#0d1f14] truncate">
                    {player.firstName} {player.lastName}
                  </p>
                  {player.position && (
                    <p className="text-xs text-zinc-500 truncate mt-0.5">{player.position}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

      </div>
    </div>
  );
}

/* ---- Language badge ---- */
function LangBadge({ language, onChange, disabled }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onChange(language === 'en' ? 'es' : 'en')}
      title={t('roster.langToggleTitle')}
      className={[
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors cursor-pointer select-none',
        language === 'es'
          ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
          : 'bg-[#b5f03d]/20 text-[#3d6010] hover:bg-[#b5f03d]/30',
      ].join(' ')}
    >
      {language === 'es' ? '🇲🇽 ES' : '🇺🇸 EN'}
    </button>
  );
}

/* ---- Per-player parent invite status badge ----
 *
 * Shows the coach a one-glance read of where each family stands:
 *   • "Joined"      — accepted invite found for this player (green)
 *   • "Invite sent" — active invite, not yet accepted (amber)
 *   • nothing       — no invite ever sent for this player
 *
 * Hover/longpress reveals the relative age of the most recent invite
 * so the coach can decide whether to nudge or re-send.
 */
function InviteStatusBadge({ invite }) {
  const { t } = useTranslation();
  if (!invite) return null;

  const status = String(invite.status || '').toLowerCase();
  // Prefer lastSentAt (updated on every re-send) over createdAt so the
  // "sent X ago" age reflects the most recent send, not the original.
  const ms = invite.lastSentAt?.toMillis?.() || invite.createdAt?.toMillis?.() || 0;
  const ageDays = ms ? Math.max(0, Math.floor((Date.now() - ms) / 86400000)) : null;
  const ageLabel = ageDays === null
    ? ''
    : ageDays === 0
      ? t('roster.ageToday')
      : ageDays === 1
        ? t('roster.ageOneDay')
        : t('roster.ageDays', { count: ageDays });

  if (status === 'accepted') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
        title={ageLabel ? t('roster.joinedAgo', { age: ageLabel }) : t('roster.parentHasJoined')}
      >
        <CheckBadgeIcon className="h-3 w-3" />
        {t('roster.joined')}
      </span>
    );
  }

  if (status === 'active') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
        title={ageLabel ? t('roster.sentAgoNotAccepted', { age: ageLabel }) : t('roster.inviteSentNotAccepted')}
      >
        <ClockIcon className="h-3 w-3" />
        {t('roster.inviteSent')}
      </span>
    );
  }

  // Revoked / expired — show nothing rather than a red "failed" pill;
  // the coach can simply re-invite without being shamed about it.
  return null;
}

/* ---- Empty state ---- */
function EmptyState({ onAdd, onImport }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-[#b5f03d]/15 ring-1 ring-[#b5f03d]/40 text-[#5a8a1f]">
        <ClipboardDocumentListIcon className="h-8 w-8" />
      </div>
      <h3
        className="text-lg font-bold uppercase tracking-wide text-[#0d1f14] mb-2"
        style={{ fontFamily: HEAD_FONT }}
      >
        {t('roster.noPlayersYet')}
      </h3>
      <p className="text-sm text-zinc-500 max-w-sm mb-8">
        {t('roster.emptyCoachDesc')}
      </p>
      <div className="flex gap-3">
        <button
          onClick={onImport}
          className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-5 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
        >
          <ArrowUpTrayIcon className="h-4 w-4" />
          {t('roster.importRoster')}
        </button>
        <button
          onClick={onAdd}
          className="inline-flex items-center gap-2 rounded-xl border border-zinc-300 bg-white px-5 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
          style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
        >
          <UserPlusIcon className="h-4 w-4" />
          {t('roster.addPlayer')}
        </button>
      </div>
    </div>
  );
}

/* ---- Main Component ---- */
export default function RosterPage() {
  const { t } = useTranslation();
  const { teamId } = useParams();
  const { firebaseUser, userProfile } = useAuth();
  const navigate = useNavigate();

  // Per-team role detection — same pattern as ViewTeam. A coach who's
  // also a parent on a different team must NOT see the coach roster
  // (full guardian PII, edit/delete, bulk-invite) when they're only
  // a parent on this team. teamRole() returns 'coach' for owner/staff,
  // 'parent' for parent-only, null otherwise.
  //
  // "No global mode" rule (Phase 1 refactor): role is a property of
  // the team relationship, NOT the user. Only check teamRole here —
  // do not OR with the profile.isParent signup hint, otherwise a
  // user who coaches Team A but is a parent on Team B would get the
  // parent view on Team A's roster. Bug fixed May 2026.
  const { teamRole } = useRole();
  const isParent = teamRole(teamId) === 'parent';

  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [langFilter, setLangFilter] = useState('all'); // 'all' | 'en' | 'es'

  // Status tab — splits the roster into All / Active / Prospects /
  // Inactive. Default is 'all' so a coach who's never set anyone to
  // Prospect/Inactive sees their full roster on first land (otherwise
  // they'd land on Active and wonder where their data went). The
  // user's pick is persisted to localStorage per team so a coach who
  // prefers Active stays there across reloads.
  const STATUS_FILTER_LS_KEY = teamId ? `scrimly.roster.statusFilter.${teamId}` : null;
  const [statusFilter, setStatusFilter] = useState(() => {
    if (!STATUS_FILTER_LS_KEY) return 'all';
    try {
      const saved = localStorage.getItem(STATUS_FILTER_LS_KEY);
      if (saved === 'all' || saved === 'active' || saved === 'prospect' || saved === 'inactive') {
        return saved;
      }
    } catch {}
    return 'all';
  });
  useEffect(() => {
    if (!STATUS_FILTER_LS_KEY) return;
    try { localStorage.setItem(STATUS_FILTER_LS_KEY, statusFilter); } catch {}
  }, [STATUS_FILTER_LS_KEY, statusFilter]);

  // Bulk-invite progress flag — disables the button while invites
  // are being created so a frustrated double-click doesn't double-fire.
  const [bulkInviting, setBulkInviting] = useState(false);

  // Modals (coach only — never opened in parent view)
  const [playerModal, setPlayerModal] = useState({ open: false, player: null });
  const [importModal, setImportModal] = useState(false);
  const [inviteParentModal, setInviteParentModal] = useState({ open: false, player: null });
  const [deletingId, setDeletingId] = useState(null);

  // Load team info
  useEffect(() => {
    if (!teamId) return;
    getDoc(doc(db, 'teams', teamId)).then((snap) => {
      if (snap.exists()) setTeam({ id: snap.id, ...snap.data() });
    });
  }, [teamId]);

  // Live player list — coaches read the full doc (`players`), parents
  // read the safe-subset mirror (`playersPublic`) so they never see
  // other parents' contact info or medical notes. The mirror is kept
  // in sync server-side by functions/modules/playerMirror.js.
  useEffect(() => {
    if (!teamId) return;
    const subcollection = isParent ? 'playersPublic' : 'players';
    const q = query(
      collection(db, 'teams', teamId, subcollection),
      orderBy('lastName', 'asc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setPlayers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      if (import.meta.env.DEV) {
        console.warn('[RosterPage] subscribe failed', { subcollection, err });
      }
      setLoading(false);
    });
    return unsub;
  }, [teamId, isParent]);

  // Per-player parent channel reachability — coach-only so parents
  // never see other-parents' app-install / opt-in state. Hook batches
  // a `users where phone in [...]` query (10 per batch). For a 30-player
  // team this is 3 Firestore reads at page load.
  const { lookup: lookupParentChannels } = useParentChannelStatus(
    isParent ? [] : players
  );

  // Per-player parent invite status. We subscribe to ALL parent invites
  // for the team (bounded set — typically <50 across team history) and
  // group by metadata.playerId client-side. This lets each row show a
  // "Joined" / "Invite sent" badge so coaches can tell at a glance which
  // families are connected without leaving the page.
  //
  // Only invites stamped with metadata.playerId by the per-player ✈️
  // modal participate. Legacy team-wide invites (no playerId) are
  // ignored here — they don't map to a specific roster row.
  const [playerInviteByPid, setPlayerInviteByPid] = useState({});
  useEffect(() => {
    if (!teamId || isParent) return;
    const q = query(
      collection(db, 'invites'),
      where('teamId', '==', teamId),
      where('audience', '==', 'parent'),
    );
    const unsub = onSnapshot(q, (snap) => {
      const byPid = {};
      snap.docs.forEach((d) => {
        const inv = { id: d.id, ...d.data() };
        const pid = inv?.metadata?.playerId;
        if (!pid) return;
        // Keep only the most recent invite per player. An accepted
        // invite always wins over an active one (in case a coach
        // re-sent after the first one already converted).
        const prev = byPid[pid];
        const prevTs = prev?.createdAt?.toMillis?.() || 0;
        const currTs = inv?.createdAt?.toMillis?.() || 0;
        const prevAccepted = prev?.status === 'accepted';
        const currAccepted = inv?.status === 'accepted';
        if (!prev) {
          byPid[pid] = inv;
        } else if (currAccepted && !prevAccepted) {
          byPid[pid] = inv;
        } else if (currAccepted === prevAccepted && currTs > prevTs) {
          byPid[pid] = inv;
        }
      });
      setPlayerInviteByPid(byPid);
    }, (err) => {
      if (import.meta.env.DEV) {
        console.warn('[RosterPage] invite subscribe failed', err);
      }
    });
    return unsub;
  }, [teamId, isParent]);

  // (The parent short-circuit moved below all hooks — see end of
  // function. Rules of Hooks: hook count must be stable across
  // renders. With the new RoleContext-driven isParent, the role
  // can flip from false→true mid-session as team data loads, so
  // an early-return here would change the hook count and trip
  // React error #300.)

  // Per-status counts for the tab UI. Default empty status = 'active'
  // so legacy player records (created before the status field
  // existed) appear under the Active tab as expected.
  const playerStatusOf = (p) => p?.status || 'active';
  const statusCounts = {
    active:   players.filter((p) => playerStatusOf(p) === 'active').length,
    prospect: players.filter((p) => playerStatusOf(p) === 'prospect').length,
    inactive: players.filter((p) => playerStatusOf(p) === 'inactive').length,
  };

  // Filtered list — combines status tab + search + language filter.
  // statusFilter === 'all' bypasses the status check so the All tab
  // shows every player regardless of status.
  const filtered = players.filter((p) => {
    if (statusFilter !== 'all' && playerStatusOf(p) !== statusFilter) return false;
    const term = search.toLowerCase();
    const matchesSearch =
      !term ||
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(term) ||
      (p.parentName || '').toLowerCase().includes(term) ||
      (p.parentEmail || '').toLowerCase().includes(term);
    const matchesLang = langFilter === 'all' || p.language === langFilter;
    return matchesSearch && matchesLang;
  });

  // Active players whose primary guardian has an email but no parent
  // invite has been sent yet. Drives the "X parents not yet invited"
  // banner and the bulk-invite button. We only consider Active here —
  // by design Prospects don't get auto-invited.
  const uninvitedActive = players.filter((p) => {
    if (playerStatusOf(p) !== 'active') return false;
    const hasInvite = !!playerInviteByPid[p.id];
    if (hasInvite) return false;
    // Either guardians[0] or legacy parentEmail counts. We invite
    // anyone we have an email for; the rest stay uninvited and the
    // coach can address those manually (phone, in-person, etc.).
    const primaryEmail =
      p?.guardians?.[0]?.email ||
      p?.parentEmail ||
      '';
    return !!primaryEmail;
  });

  // Toggle language on a player
  const toggleLanguage = useCallback(async (player, newLang) => {
    try {
      await updateDoc(doc(db, 'teams', teamId, 'players', player.id), {
        language: newLang,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      toast.error(t('roster.toastLangUpdateFailed'));
    }
  }, [teamId, t]);

  // State-aware single-player invite handler. Replaces the old
  // "always open the modal" behavior with a smarter flow that
  // matches what the coach probably wants based on the player's
  // current invite state:
  //
  //   - No invite + has guardian email → one-click server-side send
  //     (no modal needed — same UX as bulk Invite all)
  //   - No invite + no email → open modal, "no email on file" mode
  //   - Pending invite → open modal in "re-send" mode showing status
  //   - Accepted (joined) → open modal in "joined" mode with option
  //     to invite an additional guardian (co-parent, grandparent)
  //
  // The modal handles modes 2-4. Mode 1 bypasses the modal entirely
  // for speed.
  const handlePlayerInvite = async (player) => {
    if (!player) return;

    // Roster-status gate: only Active players' parents can be invited.
    // Prospects are "trying out / under evaluation" and haven't been
    // offered a spot — sending them the "added to the roster, connect
    // with the team" invite is the premature-contact case the status
    // field exists to prevent. This mirrors the bulk-invite rule, which
    // already only counts Active players. Promote to Active first.
    if (playerStatusOf(player) !== 'active') {
      const nm = `${player?.firstName || ''} ${player?.lastName || ''}`.trim() || t('roster.playerFallback');
      toast(t('roster.inviteGatedToast', { name: nm }), { icon: 'ℹ️' });
      return;
    }

    const existingInvite = playerInviteByPid[player.id];
    const status = String(existingInvite?.status || '').toLowerCase();
    const primaryEmail =
      player?.guardians?.[0]?.email ||
      player?.parentEmail ||
      '';

    // Cases that need the modal (manage existing invite or no email
    // to send to). Open it and let the modal render the right UI.
    if (status === 'active' || status === 'accepted' || !primaryEmail) {
      setInviteParentModal({ open: true, player });
      return;
    }

    // Fast path: no invite yet, has email → one-click server-side send.
    const playerName = `${player?.firstName || ''} ${player?.lastName || ''}`.trim() || t('roster.playerFallback');
    const toastId = toast.loading(t('roster.toastSendingInvite', { email: primaryEmail }));
    try {
      const { code } = await createInvite({
        teamId,
        teamName: team?.name || null,
        createdBy: firebaseUser?.uid,
        audience: 'parent',
        preset: 'parent',
        email: primaryEmail,
        expiresInDays: 30,
        label: `Parent invite — ${playerName}`,
        language: player?.guardians?.[0]?.language || player?.language || 'en',
        metadata: {
          createdVia: 'roster-row-quick-invite',
          playerName,
          playerId: player.id,
        },
      });
      await sendInviteDelivery({
        method: 'email',
        to: primaryEmail,
        inviteUrl: buildInviteUrl(code, teamId),
      });
      toast.success(t('roster.toastInviteSent', { email: primaryEmail }), { id: toastId });
    } catch (err) {
      console.error('[handlePlayerInvite] failed', err);
      toast.error(
        err?.message?.includes('deliverInvite')
          ? t('roster.toastDeliveryFailed')
          : t('roster.toastInviteFailed'),
        { id: toastId, duration: 6500 }
      );
      // Open the modal as a fallback so coach can still grab the link
      setInviteParentModal({ open: true, player });
    }
  };

  // Bulk-invite: creates a per-player parent invite for every Active
  // player whose primary guardian has an email, then fires the
  // deliverInvite cloud function (email channel) for each one. We
  // skip players who already have an invite on file. This is the
  // "I just finalized my roster" one-click action.
  const handleBulkInviteParents = async () => {
    if (uninvitedActive.length === 0) return;
    if (bulkInviting) return;

    const ok = window.confirm(
      t('roster.bulkInviteConfirm', { count: uninvitedActive.length })
    );
    if (!ok) return;

    setBulkInviting(true);
    const toastId = toast.loading(
      t('roster.bulkInviteLoading', { count: uninvitedActive.length })
    );

    let sent = 0;
    let failed = 0;
    const teamName = team?.name || null;

    for (const p of uninvitedActive) {
      try {
        const playerName = [p.firstName, p.lastName].filter(Boolean).join(' ') || t('roster.playerFallback');
        const email =
          p?.guardians?.[0]?.email ||
          p?.parentEmail ||
          '';
        if (!email) continue;

        // 1. Create the invite record (parent-audience, stamped with
        //    playerId so the per-player invite tracker picks it up
        //    and the row badge flips to "Invite sent").
        const { code } = await createInvite({
          teamId,
          teamName,
          createdBy: firebaseUser?.uid,
          audience: 'parent',
          preset: 'parent',
          email,
          expiresInDays: 30,
          label: `Parent invite — ${playerName}`,
          language: p?.guardians?.[0]?.language || p?.language || 'en',
          metadata: {
            createdVia: 'roster-bulk-invite',
            playerName,
            playerId: p.id,
          },
        });

        // 2. Fire delivery via the shared sendInviteDelivery helper
        //    (calls the deliverInvite cloud function — server-side
        //    SendGrid send, not coach's own mail client).
        await sendInviteDelivery({
          method: 'email',
          to: email,
          inviteUrl: buildInviteUrl(code, teamId),
        });

        sent += 1;
      } catch (err) {
        console.warn('[bulkInvite] failed for player', p.id, err);
        failed += 1;
      }
    }

    setBulkInviting(false);

    if (failed === 0) {
      toast.success(
        t('roster.bulkInviteAllSuccess', { count: sent }),
        { id: toastId, duration: 5000 }
      );
    } else if (sent > 0) {
      toast.success(
        t('roster.bulkInvitePartial', { sent, failed }),
        { id: toastId, duration: 6000 }
      );
    } else {
      toast.error(
        t('roster.bulkInviteAllFailed'),
        { id: toastId }
      );
    }
  };

  // Bulk set language
  const bulkSetLanguage = async (lang) => {
    if (filtered.length === 0) return;
    const label = lang === 'es' ? t('roster.langSpanish') : t('roster.langEnglish');
    try {
      await Promise.all(
        filtered.map((p) =>
          updateDoc(doc(db, 'teams', teamId, 'players', p.id), {
            language: lang,
            updatedAt: serverTimestamp(),
          })
        )
      );
      toast.success(t('roster.bulkLangSuccess', { count: filtered.length, lang: label }));
    } catch {
      toast.error(t('roster.bulkLangFailed'));
    }
  };

  // Delete player
  const handleDelete = async (player) => {
    if (!window.confirm(
      t('roster.removeConfirm', { firstName: player.firstName, lastName: player.lastName })
    )) return;
    setDeletingId(player.id);
    try {
      await deleteDoc(doc(db, 'teams', teamId, 'players', player.id));
      toast.success(t('roster.toastPlayerRemoved'));
    } catch {
      toast.error(t('roster.toastPlayerRemoveFailed'));
    } finally {
      setDeletingId(null);
    }
  };

  // Save player (add or edit). The optional 3rd arg lets the modal
  // signal "keep me open" — used by the "Save & Add Another" button
  // so a coach building a 15-kid roster can rapid-fire entries
  // without re-opening the modal each time.
  const handleSavePlayer = async (data, existingId, options = {}) => {
    if (existingId) {
      await updateDoc(doc(db, 'teams', teamId, 'players', existingId), {
        ...data,
        updatedAt: serverTimestamp(),
      });
      toast.success(t('roster.toastPlayerUpdated'));
    } else {
      const wasEmpty = players.length === 0;
      await addDoc(collection(db, 'teams', teamId, 'players'), {
        ...data,
        source: 'manual',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      toast.success(t('roster.toastPlayerAdded'));
      if (wasEmpty) {
        track(ACTIVATION_EVENTS.ROSTER_ADDED, {
          team_id: teamId,
          source: 'manual',
          count: 1,
        });
      }
    }
    if (!options.keepOpen) {
      setPlayerModal({ open: false, player: null });
    }
  };

  // Import complete callback
  const handleImportComplete = async (rows) => {
    if (!rows.length) return;
    const wasEmpty = players.length === 0;
    let count = 0;
    for (const row of rows) {
      await addDoc(collection(db, 'teams', teamId, 'players'), {
        ...row,
        source: 'import',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      count++;
    }
    toast.success(t('roster.toastImported', { count }));
    if (wasEmpty && count > 0) {
      track(ACTIVATION_EVENTS.ROSTER_ADDED, {
        team_id: teamId,
        source: 'import',
        count,
      });
    }
    setImportModal(false);
  };

  const enCount = players.filter((p) => p.language !== 'es').length;
  const esCount = players.filter((p) => p.language === 'es').length;

  // Parent connection stats for the page header. Counts each player at most
  // once (using the most-recent invite stored in playerInviteByPid).
  const joinedCount = useMemo(
    () => players.filter((p) => playerInviteByPid[p.id]?.status === 'accepted').length,
    [players, playerInviteByPid]
  );
  const pendingCount = useMemo(
    () => players.filter((p) => playerInviteByPid[p.id]?.status === 'active').length,
    [players, playerInviteByPid]
  );

  // Parents get a stripped-down read-only view — no PII leakage of
  // other parents' contact info, no management controls. We branch
  // HERE (after all hooks) instead of with an early return at the
  // top of the function so the hook count stays stable across
  // renders (Rules of Hooks). isParent can flip from false→true
  // when team data loads (RoleContext.teamRole(teamId) goes from
  // null → 'parent'), and an early return would change the hook
  // count between those two renders → React error #300.
  if (isParent) {
    return (
      <ParentRosterView
        team={team}
        players={players}
        loading={loading}
        teamId={teamId}
        navigate={navigate}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#f7faf5] p-4 sm:p-6 lg:p-8">

      {/* Team-page sub-navigation — same tab bar that lives on
          ViewTeam (Overview), parent's roster view, and team chat
          redirect. Sticky so it stays accessible while scrolling
          through long rosters. */}
      <TeamSubNav teamId={teamId} />

      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/teams/${teamId}`)}
          className="mb-3 inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-[#5a8a1f] transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          {team?.name || t('roster.teamFallback')}
        </button>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p
              className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {t('roster.eyebrowTeam')}
            </p>
            <h1
              className="text-2xl font-bold uppercase tracking-tight text-[#0d1f14]"
              style={{ fontFamily: HEAD_FONT }}
            >
              {t('roster.titleCoach')}
            </h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              {t('roster.headerStats', { count: players.length, en: enCount, es: esCount })}
              {(joinedCount > 0 || pendingCount > 0) && (
                <>
                  {' · '}
                  <span className="text-green-700 font-semibold">
                    {t('roster.joinedCount', { count: joinedCount })}
                  </span>
                  {pendingCount > 0 && (
                    <>
                      {' · '}
                      <span className="text-amber-700 font-semibold">
                        {t('roster.pendingCount', { count: pendingCount })}
                      </span>
                    </>
                  )}
                </>
              )}
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              onClick={() => setImportModal(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-zinc-300 bg-white px-4 py-2.5 text-sm font-bold uppercase text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-all"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              {t('roster.import')}
            </button>
            <button
              onClick={() => setPlayerModal({ open: true, player: null })}
              className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2.5 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97]"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <UserPlusIcon className="h-4 w-4" />
              {t('roster.addPlayer')}
            </button>
          </div>
        </div>
      </div>

      {/* Status tabs — splits the roster into All / Active / Prospects
          / Inactive. "All" is the default so coaches who haven't used
          the status feature still see their full roster (otherwise
          they'd think their data was missing). Selection is persisted
          to localStorage per team. Each tab shows its count so empty
          groups stay discoverable. */}
      {players.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-1 rounded-xl border border-zinc-200 bg-white p-1 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
          {[
            { value: 'all',      label: t('roster.tabAll'),       count: players.length },
            { value: 'active',   label: t('roster.tabActive'),    count: statusCounts.active },
            { value: 'prospect', label: t('roster.tabProspects'), count: statusCounts.prospect },
            { value: 'inactive', label: t('roster.tabInactive'),  count: statusCounts.inactive },
          ].map((tab) => {
            const isActive = statusFilter === tab.value;
            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setStatusFilter(tab.value)}
                className={clsx(
                  'inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-bold uppercase transition-colors',
                  isActive
                    ? 'bg-[#b5f03d] text-[#0d1f14]'
                    : 'text-zinc-500 hover:text-[#0d1f14] hover:bg-zinc-100'
                )}
                style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
              >
                {tab.label}
                <span className={clsx(
                  'inline-flex items-center justify-center rounded-md px-1.5 py-0.5 text-[10px] font-bold min-w-[20px]',
                  isActive ? 'bg-[#0d1f14]/10 text-[#0d1f14]' : 'bg-zinc-200 text-zinc-600'
                )}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Uninvited-parents banner — shows on All + Active tabs (the
          two tabs where active players are visible). Hidden on
          Prospects / Inactive because those statuses by design
          aren't part of the bulk-invite flow. */}
      {(statusFilter === 'all' || statusFilter === 'active') && uninvitedActive.length > 0 && (
        <div className="mb-4 rounded-xl border border-[#b5f03d]/40 bg-gradient-to-br from-[#b5f03d]/10 to-white p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/25">
                <UserPlusIcon className="h-5 w-5 text-[#3d6010]" />
              </div>
              <div className="min-w-0">
                <p
                  className="uppercase text-[#0d1f14]"
                  style={{ fontFamily: HEAD_FONT, fontWeight: 800, fontSize: 14, letterSpacing: '0.5px' }}
                >
                  {t('roster.parentsNotYetInvited', { count: uninvitedActive.length })}
                </p>
                <p className="text-xs text-zinc-600 mt-0.5">
                  {t('roster.uninvitedDesc')}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={handleBulkInviteParents}
              disabled={bulkInviting}
              className="inline-flex items-center gap-2 rounded-xl bg-[#b5f03d] hover:bg-[#94cc28] text-[#0d1f14] px-4 py-2 text-sm font-bold uppercase shadow-[0_1px_2px_0_rgba(13,31,20,0.08)] transition-all active:scale-[0.97] disabled:opacity-60 disabled:cursor-wait flex-shrink-0"
              style={{ fontFamily: HEAD_FONT, letterSpacing: '0.6px' }}
            >
              <UserPlusIcon className="h-4 w-4" />
              {bulkInviting ? t('roster.bulkInviteWorking') : t('roster.bulkInviteAll')}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      {players.length > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <input
              type="text"
              placeholder={t('roster.searchByNameParentEmail')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded-xl border border-zinc-200 bg-white pl-9 pr-4 py-2 text-sm text-[#0d1f14] placeholder-zinc-400 shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] focus:outline-none focus:border-[#b5f03d] focus:ring-2 focus:ring-[#b5f03d]/30"
            />
          </div>

          {/* Language filter */}
          <div className="flex rounded-xl border border-zinc-200 overflow-hidden flex-shrink-0">
            {[
              { value: 'all', label: t('roster.langFilterAll') },
              { value: 'en', label: '🇺🇸 EN' },
              { value: 'es', label: '🇲🇽 ES' },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setLangFilter(opt.value)}
                className={[
                  'px-3 py-2 text-xs font-bold uppercase tracking-wide transition-colors',
                  langFilter === opt.value
                    ? 'bg-[#0d1f14] text-white'
                    : 'bg-white text-zinc-600 hover:bg-[#b5f03d]/10',
                ].join(' ')}
                style={{ fontFamily: HEAD_FONT }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Bulk actions */}
          {filtered.length > 0 && (
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => bulkSetLanguage('es')}
                className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-bold uppercase tracking-wide text-amber-800 hover:bg-amber-100 transition-colors"
                style={{ fontFamily: HEAD_FONT }}
              >
                {t('roster.setAllES')}
              </button>
              <button
                onClick={() => bulkSetLanguage('en')}
                className="rounded-xl border border-zinc-300 bg-white px-3 py-2 text-xs font-bold uppercase tracking-wide text-[#0d1f14] hover:border-[#b5f03d] hover:bg-[#b5f03d]/10 transition-colors"
                style={{ fontFamily: HEAD_FONT }}
              >
                {t('roster.setAllEN')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-zinc-100 animate-pulse" />
          ))}
        </div>
      ) : players.length === 0 ? (
        <EmptyState
          onAdd={() => setPlayerModal({ open: true, player: null })}
          onImport={() => setImportModal(true)}
        />
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-zinc-400">
          {t('roster.noMatches')}
        </div>
      ) : (
        <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden shadow-[0_1px_2px_0_rgba(13,31,20,0.04)]">
          {/* Table header */}
          <div
            className="hidden sm:grid grid-cols-[2fr_2fr_1.5fr_0.75fr_auto] gap-4 px-6 py-3 border-b border-zinc-100 text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            <span>{t('roster.colPlayer')}</span>
            <span>{t('roster.colParent')}</span>
            <span>{t('roster.colEmail')}</span>
            <span>{t('roster.colLang')}</span>
            <span />
          </div>

          {/* Player rows */}
          <ul className="divide-y divide-zinc-100">
            {filtered.map((player) => (
              <li
                key={player.id}
                className="grid grid-cols-1 sm:grid-cols-[2fr_2fr_1.5fr_0.75fr_auto] gap-2 sm:gap-4 px-6 py-4 items-center hover:bg-[#b5f03d]/5 transition-colors"
              >
                {/* Player avatar + name */}
                <div className="flex items-center gap-3 min-w-0">
                  <PlayerAvatar
                    photoURL={player.photoURL}
                    jerseyNumber={player.jerseyNumber}
                    firstName={player.firstName}
                    lastName={player.lastName}
                    size={40}
                    alt={t('roster.photoAlt', { name: `${player.firstName || ''} ${player.lastName || ''}`.trim() })}
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[#0d1f14] truncate">
                      {player.firstName} {player.lastName}
                      {player.jerseyNumber && (
                        <span className="ml-2 text-xs text-zinc-400">#{player.jerseyNumber}</span>
                      )}
                    </p>
                    {player.position && (
                      <p className="text-xs text-zinc-400 truncate">{player.position}</p>
                    )}
                  </div>
                </div>

                {/* Parent info + invite status + channel reachability */}
                <div>
                  {player.parentName && (
                    <p className="text-sm text-zinc-700">{player.parentName}</p>
                  )}
                  {player.parentPhone && (
                    <p className="text-xs text-zinc-400">{player.parentPhone}</p>
                  )}
                  {!isParent && (
                    <ChannelBadges {...lookupParentChannels(player)} />
                  )}
                  {playerInviteByPid[player.id] && (
                    <div className="mt-1">
                      <InviteStatusBadge invite={playerInviteByPid[player.id]} />
                    </div>
                  )}
                </div>

                {/* Email */}
                <div>
                  {player.parentEmail ? (
                    <p className="text-sm text-zinc-600 truncate">{player.parentEmail}</p>
                  ) : (
                    <span className="text-xs text-zinc-300">—</span>
                  )}
                </div>

                {/* Language toggle */}
                <div>
                  <LangBadge
                    language={player.language || 'en'}
                    onChange={(newLang) => toggleLanguage(player, newLang)}
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 justify-end">
                  {(() => {
                    // State-aware send icon:
                    //   accepted (joined) → green check, opens "joined" modal
                    //   active (pending)  → amber resend arrow, opens re-send modal
                    //   none + has email  → lime send icon, ONE-CLICK send
                    //   none + no email   → outlined send icon, opens "copy link" modal
                    const inv = playerInviteByPid[player.id];
                    const status = String(inv?.status || '').toLowerCase();
                    const primaryEmail =
                      player?.guardians?.[0]?.email ||
                      player?.parentEmail ||
                      '';
                    // Only Active players can be invited (Prospects are
                    // under evaluation; Inactive have left). Show the icon
                    // disabled with a "promote first" tooltip otherwise.
                    const canInvite = playerStatusOf(player) === 'active';

                    let title;
                    let iconClass;
                    let Icon = PaperAirplaneIcon;
                    if (!canInvite) {
                      title = t('roster.inviteGatedTooltip');
                      iconClass = 'text-zinc-300 cursor-not-allowed';
                    } else if (status === 'accepted') {
                      title = t('roster.iconJoinedSendAnother');
                      iconClass = 'text-emerald-600 hover:bg-emerald-100';
                      Icon = CheckCircleIcon;
                    } else if (status === 'active') {
                      title = t('roster.iconResendTo', { name: player.parentName || t('roster.parentFallback') });
                      iconClass = 'text-amber-600 hover:bg-amber-100';
                      Icon = ArrowPathIcon;
                    } else if (primaryEmail) {
                      title = t('roster.iconSendTo', { email: primaryEmail });
                      iconClass = 'text-[#3d6010] hover:bg-[#b5f03d]/15';
                    } else {
                      title = t('roster.iconNoEmail');
                      iconClass = 'text-zinc-400 hover:bg-zinc-100';
                    }

                    return (
                      <button
                        onClick={() => handlePlayerInvite(player)}
                        disabled={!canInvite}
                        className={`p-1.5 rounded-lg transition-colors ${iconClass}`}
                        title={title}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    );
                  })()}
                  <button
                    onClick={() => setPlayerModal({ open: true, player })}
                    className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                    title={t('roster.edit')}
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(player)}
                    disabled={deletingId === player.id}
                    className="p-1.5 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                    title={t('roster.remove')}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>

          {/* Footer count */}
          {filtered.length < players.length && (
            <div className="border-t border-zinc-100 px-6 py-3 text-xs text-zinc-400">
              {t('roster.showingOf', { shown: filtered.length, total: players.length })}
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {playerModal.open && (
        <PlayerModal
          player={playerModal.player}
          // Sport feeds the position-suggestion datalist so a soccer
          // coach sees soccer positions, basketball coach sees basketball
          // positions, etc. Falls back to the union when sport is unset
          // or unrecognized so the dropdown is never empty.
          sport={team?.sport || team?.sportType}
          // Roster feeds the jersey-number conflict detector inside
          // PlayerModal — it warns when a coach assigns a number that
          // another player already has. Pass minimal info to avoid
          // leaking unrelated player fields into the modal.
          roster={players.map((p) => ({
            id: p.id,
            jerseyNumber: p.jerseyNumber,
            displayName: [p.firstName, p.lastName].filter(Boolean).join(' ') || t('roster.unnamedPlayer'),
          }))}
          onSave={handleSavePlayer}
          onClose={() => setPlayerModal({ open: false, player: null })}
        />
      )}

      {importModal && (
        <ImportModal
          teamId={teamId}
          onImport={handleImportComplete}
          onClose={() => setImportModal(false)}
        />
      )}

      {/* Per-player parent invite. Generates a single-use invite stamped
          with metadata.playerName so the AcceptInvite landing personalizes
          the celebration ("Jordan has been invited!"). */}
      <InviteParentForPlayerModal
        open={inviteParentModal.open}
        onClose={() => setInviteParentModal({ open: false, player: null })}
        teamId={teamId}
        teamName={team?.name || team?.teamName || ''}
        player={inviteParentModal.player}
        uid={firebaseUser?.uid}
        // Pass the player's current invite (or undefined) so the
        // modal renders the right state — re-send for pending,
        // joined view for accepted, manual link for no-email. The
        // modal NEVER auto-creates a duplicate invite when one
        // already exists; that was the source of the duplicate
        // invite bug we're fixing here.
        existingInvite={
          inviteParentModal.player
            ? playerInviteByPid[inviteParentModal.player.id]
            : undefined
        }
      />
    </div>
  );
}
